import type { WireAuditRow } from '@/api/wire';
import { DENIALS, type Denial } from '@/api/denials';
import { toolName } from '@/app/technical';

/**
 * The audit log in words a person reads (§7). A receipt records a code —
 * `assignment.granted`, `agent:otis.agent@…` — because the chain and the
 * agents that read it need one exact, stable string. A person needs a
 * sentence. The codes stay on the wire, untouched, for agents and for the
 * superadmin's detail; the page says what happened.
 */
const ACTION_WORDS: Record<string, string> = {
  'agent.restore': 'Asked to restore an agent',
  'agent.restored': 'Restored an agent',
  'agent.revoke': 'Asked to stop an agent',
  'agent.revoked': 'Stopped an agent',
  'assignment.ended': 'Ended an assignment',
  'assignment.granted': 'Gave an assignment',
  'assignment.granted_by_admin': 'Gave an assignment',
  'attachment.drive_linked': 'Linked a file from Drive',
  'attachment.uploaded': 'Attached a file',
  'autonomy.change': 'Changed how freely a department acts',
  'autonomy.lowered': 'Lowered how freely a department acts',
  'autonomy.raised': 'Raised how freely a department acts',
  'billing.change': 'Changed billing',
  'booking.held': 'Held a booking',
  'booking.page': 'Opened a booking page',
  'booking_link.created': 'Made a booking link',
  'booking_link.retired': 'Retired a booking link',
  'build.approved': 'Approved a change',
  'build.returned': 'Sent a change back',
  'campaign.approve': 'Asked to approve a campaign',
  'campaign.approved': 'Approved a campaign',
  'campaign.created': 'Drew up a campaign',
  'campaign.ended': 'Ended a campaign',
  'capture.off': 'Stopped recording mail',
  'capture.on': 'Started recording mail',
  'change.abandoned': 'Dropped a change',
  'change.asked': 'Asked for a change',
  'change.confirmed': 'Confirmed a change',
  'client.admit': 'Asked to connect an app',
  'client.admitted': 'Connected an app',
  'client.withdrawn': 'Disconnected an app',
  'commitment.dismissed': 'Dismissed a promise',
  'commitments.confirmed': 'Confirmed promises',
  'comms.calendar_acl': 'Read who sees a calendar',
  'comms.calendar_channel_stop': 'Stopped watching a calendar',
  'comms.calendar_freebusy': 'Checked when people are free',
  'comms.calendar_hold': 'Held time in a calendar',
  'comms.calendar_hold_cancel': 'Cancelled a calendar hold',
  'comms.calendar_invite': 'Sent a calendar invitation',
  'comms.calendar_invite_update': 'Updated a calendar invitation',
  'comms.calendar_read': 'Read a calendar',
  'comms.calendar_subscriptions': 'Read calendar subscriptions',
  'comms.calendar_watch_acl': 'Watched who sees a calendar',
  'comms.calendar_watch_busy': 'Watched a calendar for busy times',
  'comms.calendar_watch_events': 'Watched a calendar for events',
  'comms.calendar_watch_list': 'Watched the calendar list',
  'comms.chat_post': 'Posted in Google Chat',
  'comms.drive_export': 'Read a Drive document',
  'comms.drive_list': 'Listed a Drive folder',
  'comms.gmail_delegates': 'Read a mailbox’s delegates',
  'comms.gmail_draft_create': 'Wrote a draft in Gmail',
  'comms.gmail_draft_get': 'Read a Gmail draft',
  'comms.gmail_get': 'Read an email',
  'comms.gmail_history': 'Read what changed in a mailbox',
  'comms.gmail_list': 'Listed a mailbox',
  'comms.gmail_send': 'Sent an email',
  'comms.gmail_thread_get': 'Read an email thread',
  'comms.gmail_watch': 'Watched a mailbox',
  'comms.twin_gmail_draft_create': 'Wrote a draft in its person’s Gmail',
  'comms.twin_gmail_get': 'Read an email in its person’s inbox',
  'comms.twin_gmail_history': 'Read what changed in its person’s inbox',
  'comms.twin_gmail_watch': 'Watched its person’s inbox',
  'company.updated': 'Updated a company',
  'connection.key_revoked': 'Removed a service’s key',
  'connection.key_set': 'Put in a service’s key',
  'connection.rotation_term_lengthened': 'Gave a key longer before it must change',
  'connection.rotation_term_shortened': 'Gave a key less time before it must change',
  'constitution.amend': 'Amended the rulebook',
  'contact.assigned': 'Assigned a contact',
  'contact.dismissed': 'Dismissed a contact',
  'contact.promoted': 'Approved a contact',
  'contact.updated': 'Updated a contact',
  'conversation.drafted': 'Drafted a reply',
  'conversation.sent': 'Sent a reply',
  'counterparty.approve': 'Asked to approve a company',
  'counterparty.approved': 'Approved a company',
  'cover.ended': 'Ended cover for a colleague',
  'cover.set': 'Set cover for a colleague',
  'data.erase': 'Erased a person’s data',
  'data.export_production': 'Exported production data',
  'delegation.grant': 'Asked to delegate a mailbox',
  'delegation.granted': 'Delegated a mailbox',
  'delegation.revoked': 'Ended a mailbox delegation',
  'department.create': 'Asked to create a department',
  'agent.create': 'Asked to add an agent',
  'department.created': 'Created a department',
  'agent.declared': 'Added an agent to the ledger',
  'department.ledger_committed': 'Recorded a department in the ledger',
  'department.resume': 'Asked to restart a department',
  'department.resumed': 'Restarted a department',
  'department.suspend': 'Asked to stop a department',
  'department.suspended': 'Stopped a department',
  'directory.create_orgunit': 'Made a Workspace folder of accounts',
  'directory.create_user': 'Made a Workspace account',
  'directory.group_membership': 'Changed a group’s members',
  'directory.modify_user': 'Changed a Workspace account',
  'draft.prepared': 'Prepared a draft',
  'draft.redraft_asked': 'Asked for a draft written again, their way',
  'draft.written_to_mailbox': 'Put a draft in a mailbox',
  'employment.moved': 'Moved someone to another company',
  'erasure.executed': 'Carried out an erasure',
  'erasure.refused': 'Refused an erasure',
  'erasure.requested': 'Recorded an erasure request',
  'file.door': 'Filed a document',
  'human.departed': 'Ended a person’s access',
  'iam.grant_scope': 'Granted a permission',
  'identity.revoke_human': 'Ended a person’s access',
  'incident.acknowledged': 'Acknowledged an incident',
  'killswitch.change': 'Changed the stop switch',
  'ledger.edit_kept': 'Kept a ledger edit',
  'lifecycle.basis_recorded': 'Recorded why we may write to someone',
  'lifecycle.basis_withdrawn': 'Withdrew why we may write to someone',
  'lifecycle.deleted': 'Deleted someone from the mailing tool',
  'lifecycle.identified': 'Sent someone to the mailing tool',
  'lifecycle.imported': 'Imported people to the mailing tool',
  'lifecycle.reconciled': 'Checked the mailing tool against our records',
  'lifecycle.tracked': 'Sent an event to the mailing tool',
  'lifecycle.unsubscribed': 'Recorded an unsubscribe',
  'mail.drafted': 'Drafted an email',
  'mail.mirror_ended': 'Stopped copying a mailbox',
  'mail.mirrored': 'Copied new mail',
  'mailing.approve': 'Asked to approve a mailing',
  'mailing.approved': 'Approved a mailing',
  'mailing.created': 'Drew up a mailing',
  'mailing.ended': 'Ended a mailing',
  'market.opened': 'Opened a country for outreach',
  'mcp.call': 'Was used from another app',
  'meeting_note.read': 'Read meeting notes',
  'message.queued': 'Queued a message',
  'message.recorded': 'Recorded a message',
  'message.sent': 'Sent a message',
  'notify.channel_added': 'Added a way to be told',
  'notify.channel_removed': 'Removed a way to be told',
  'oauth.token': 'Signed an app in',
  'person.invitation_withdrawn': 'Withdrew an invitation',
  'person.invited': 'Invited a person',
  'guidance.adopted': 'Adopted writing guidance for the department',
  'guidance.declined': 'Declined writing guidance for the department',
  'guidance.revoked': 'Stopped following writing guidance',
  'guidance.written': 'Said how drafts should be written',
  'plan.approved': 'Approved a prospecting plan',
  'plan.dry_run': 'Tried a prospecting plan without contacting anyone',
  'plan.swept': 'Searched for places for a plan',
  'plan.covered': 'Ended a plan once its whole territory was searched',
  'project.meeting_abandoned': 'Closed a meeting that could not go on',
  'project.meeting_convened': 'Called a meeting in a project',
  'project.member': 'Added someone to a project',
  'project.member_added': 'Added someone to a project',
  'project.member_removed': 'Took someone off a project',
  'project.opened': 'Opened a project',
  'project.said': 'Spoke in a project',
  'project.tool_grant': 'Lent a tool to a project',
  'project.tool_granted': 'Lent a tool to a project',
  'project.tool_revoked': 'Took a tool back from a project',
  'promotion.promoted': 'Put a change live',
  'promotion.proposed': 'Proposed putting a change live',
  'promotion.rejected': 'Turned down putting a change live',
  'prospect.discovered': 'Found a place',
  'prospect.drafted': 'Drafted a first email to a place',
  'prospect.enriched': 'Found an address at a place',
  'prospect.excluded': 'Took a place off the list',
  'prospect.imported': 'Added a place from research',
  'prospect.opted_out': 'Recorded that a place opted out',
  'prospect.read': 'Read a place’s website',
  'prospect.retried': 'Tried a prospect again',
  'prospect.watched': 'Checked a place’s website for changes',
  'publication.drafted': 'Drafted a post',
  'request.accepted': 'Accepted a request',
  'request.acknowledged': 'Acknowledged a request',
  'request.cancelled': 'Cancelled a request',
  'request.completed': 'Finished a request',
  'request.opened': 'Asked another department',
  'request.received': 'Received a request',
  'request.refused': 'Turned down a request',
  'request.result_received': 'Received an answer to a request',
  'rollback.breached': 'Went past an agreed limit and stopped',
  'secret.read': 'Read a stored key',
  'secret.rotate': 'Changed a stored key',
  'sequence.approved': 'Approved a series of emails',
  'sequence.retired': 'Retired a series of emails',
  'sequence.written': 'Wrote a series of emails',
  'setup.marked_done': 'Marked a setup step done',
  'setup.marked_not_done': 'Marked a setup step not done',
  'shortlist.autoconfirm_started': 'Let shortlists confirm themselves on their record again',
  'operation.approved': 'Approved a change to the platform',
  'operation.refused': 'Refused a change to the platform',
  'operation.earned_stopped': 'Stopped a platform check running on its record',
  'operation.earned_started': 'Let a platform check run on its record again',
  'operation.ran_on_record': 'A platform check ran on its record',
  'operation.read': 'Read the platform’s cloud project or repository',
  'operation.requested': 'Asked for a platform operation',
  'operation.standing_confirmed': 'Confirmed the twin’s standing assignment',
  'operation.standing_ended': 'Ended the twin’s standing assignment',
  'brief.issue_opened': 'Opened an engineering brief as an issue',
  'nightly.started': 'Turned the twin’s nightly audit on',
  'nightly.stopped': 'Turned the twin’s nightly audit off',
  'nightly.read_platform': 'Read the platform’s queues for the nightly audit',
  'nightly.halted': 'Did not audit the platform overnight, because it was stopped',
  'platform_reach.read': 'Read a department’s or a person’s record, as a superadmin’s twin',
  'shortlist.autoconfirm_stopped': 'Stopped shortlists confirming themselves',
  'shortlist.confirmed': 'Confirmed a shortlist an assistant proposed',
  'shortlist.confirmed_by_record': 'A shortlist confirmed itself on its record',
  'shortlist.declined': 'Declined a shortlist an assistant proposed',
  'shortlist.resolved': 'Looked up the businesses of a shortlist',
  'evidence_proposal.confirmed': 'Confirmed a finding an assistant proposed',
  'evidence_proposal.declined': 'Declined a finding an assistant proposed',
  'evidence_proposal.checked': 'Read the pages a proposed finding cites',
  'strategy.used': 'Set up a plan from a proposed strategy',
  'strategy.template': 'Kept a proposed strategy as a template',
  'strategy.declined': 'Declined a proposed strategy',
  'skill.proposed': 'Proposed a new skill',
  'source.read': 'Read a market source',
  'subject.link_confirmed': 'Confirmed who an email is about',
  'supervision.minted': 'Made an approval code',
  'suppression.added': 'Stopped writing to an address',
  'suppression.lift': 'Asked to write to an address again',
  'suppression.lift_refused': 'Refused writing to an address again',
  'suppression.lift_requested': 'Asked to write to an address again',
  'suppression.lifted': 'Started writing to an address again',
  'suppression.one_click': 'Unsubscribed in one click',
  'task.asked_of_agent': 'Gave an agent a task',
  'task.done': 'Finished a task',
  'task.closed_done': 'Marked a task done',
  'task.closed_cancelled': 'Said a task is no longer needed',
  'task.created_for': 'Made a task for someone in the department',
  'task.reassigned': 'Handed a task on to someone else',
  'task.removed': 'Removed a task, saying why',
  'thread.removed': 'Took a conversation off the day, saying why',
  'mail_rule.added': 'Said which mail is not work',
  'mail_rule.revoked': 'Took back a rule about mail',
  'agent_direction.added': 'Gave the department’s agents a direction',
  'agent_direction.revoked': 'Took back a direction to the agents',
  'away.set': 'Said when someone is away',
  'away.ended': 'Ended someone’s time away',
  'plan.rests_set': 'Said when a prospecting plan rests',
  'supplier.candidate_added': 'Added a manufacturer found on a public page',
  'supplier.contact_added': 'Added a manufacturer’s sales contact, read from a page',
  'supplier.shortlist': 'Shortlisted a manufacturer to compare',
  'supplier.unshortlist': 'Took a manufacturer off the shortlist',
  'supplier.reject': 'Rejected a manufacturer, with a reason',
  'supplier.chosen': 'Chose a manufacturer as the main or the backup',
  'supplier.release': 'Released a manufacturer from being the main or the backup',
  'supplier.contacted': 'Said a manufacturer had been written to',
  'process.proposed': 'Offered to search for manufacturers on a schedule, after ten were taken through by hand',
  'process.turn_on': 'Turned on the monthly manufacturer search',
  'process.decline': 'Declined the offer of a monthly manufacturer search',
  'process.turn_off': 'Turned off the monthly manufacturer search',
  'supplier.email_prepared': 'Had the email to a manufacturer put in their own Gmail drafts',
  'suppliers.researched': 'Wrote a search for manufacturers onto the supplier list',
  'plan.probe_cap_set': 'Capped how many of another kind of buyer a plan probes',
  'sequence.review_asked': 'Asked a colleague to read a first-touch template',
  'sequence.reviewed': 'Read a first-touch template and said how it reads',
  'signal.supply_risk': 'Wrote down a risk to a harvest where the product grows',
  'cadence.call_recorded': 'Recorded what came of a follow-up call',
  'supplier_sample.received': 'Recorded a supplier’s sample as received',
  'supplier_sample.judged': 'Judged a supplier’s sample',
  'buyer_answer.recorded': 'Wrote down what a buyer said',
  'agreement.created': 'Added an agreement',
  'agreement.review_asked': 'Asked someone to review an agreement',
  'agreement.reviewed': 'Reviewed an agreement',
  'agreement.signed': 'Marked an agreement signed',
  'agreement.withdrawn': 'Withdrew an agreement',
  'event.created': 'Added a fair or event',
  'event.attendee_added': 'Said someone goes to an event',
  'event.attendee_removed': 'Said someone no longer goes to an event',
  'event.contact_met': 'Recorded someone met at an event',
  'funding.created': 'Added a grant, programme or investor',
  'funding.moved': 'Moved a funding opportunity on',
  'requirement.added': 'Added what a market requires',
  'requirement.moved': 'Updated what a market requires',
  'task.returned': 'Handed a task back to a person',
  'task.earned_start': 'Earned the right to start a routine task alone',
  'task.earned_started': 'Started a routine task alone',
  'task.earned_stopped': 'Stopped starting a routine task alone',
  'task.material_read': 'Read what its person attached',
  'task.returned_to_lead': 'Handed a task back to the department’s lead',
  'task.returned_to_principal': 'Handed a task back to its person',
  'task.tool_grant': 'Lent a tool to a task',
  'task.tool_granted': 'Lent a tool to a task',
  'task.tool_granted_automatically': 'Lent a tool to a task on its record',
  'task.tool_refused': 'Refused a tool to a task',
  'task.tool_revoked': 'Took a tool back from a task',
  'task.waiting': 'Waited for a tool',
  'term.lengthened': 'Let grants run longer',
  'term.shortened': 'Made grants end sooner',
  'thread.claim_refused': 'Was refused a conversation',
  'thread.claimed': 'Took a conversation',
  'thread.merge_declined': 'Kept two conversations apart',
  'thread.merged': 'Joined two conversations',
  'thread.unclaimed': 'Let a conversation go',
  'token.minted': 'Made an approval code',
  'token.revoked': 'Cancelled an approval code',
  'tool.admit': 'Asked to allow a tool',
  'tool.admitted': 'Allowed a tool',
  'tool.call': 'Used a tool',
  'tool.called': 'Used a tool',
  'tool.withdraw': 'Asked to stop a tool',
  'tool.withdrawn': 'Stopped a tool',
  'toolset.assigned': 'Gave a set of tools',
  'toolset.chosen': 'Chose a set of tools',
  'toolset.preset_defined': 'Defined a set of tools',
  'twin.task': 'Worked on a task for its person',
  'twin.task_notice': 'Told the superadmin a task is blocked',
  'twin_task.confirmed': 'Confirmed a task for their twin',
  'twin_task.planned': 'Planned a task',
  'vendor.allowance_lowered': 'Lowered what a service may cost',
  'vendor.allowance_raised': 'Raised what a service may cost',
  'vendor.paused': 'Paused a service',
  'vendor.plan_recorded': 'Recorded a service’s price plan',
  'vendor.policy': 'Changed a service’s rules',
  'vendor.resumed': 'Resumed a service',
};

/** A code nobody has written words for yet still reads as words, never as a code. */
function humanise(code: string): string {
  const words = code.replace(/[._:]+/g, ' ').replace(/\s+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Did something';
}

/** Every code with words, by its words, for a picker a person chooses from. */
export const ACTION_CHOICES: ReadonlyArray<{ code: string; words: string }> = Object.entries(ACTION_WORDS)
  .map(([code, words]) => ({ code, words }))
  .sort((a, b) => a.words.localeCompare(b.words) || a.code.localeCompare(b.code));

/** What happened, as a sentence without its object: "Gave an assignment". */
export function actionWords(action: string): string {
  return ACTION_WORDS[action] ?? humanise(action);
}

/**
 * The rules core-api refuses by itself, beside the gateway's (`DENIALS`):
 * what a refusal on the chain was made under, in words (ADR-0165).
 */
const OWN_RULES: Record<string, Denial> = {
  supervision_invalid: {
    title: 'The approval code did not verify',
    body: 'A critical act needs a single-use code a person mints for exactly that act and target. This one was mistyped, already spent, expired, or minted for something else, so nothing was done.',
    who: 'Superadmin',
    tone: 'warning',
  },
};

/**
 * The rule a refused row was made under, and the action without it.
 *
 * From 0163 the rule is its own column. A gateway refusal written before
 * it carried the rule as the action's last segment (`tool.call.x.tool_not_admitted`),
 * so that is read too, but only when the segment is one of the gateway's own
 * reasons: `counterparty.approved` ends in a segment that is no rule.
 */
/** Whether a rule is one the Refusals page lists — its own entry, never a prototype's key. */
export function isCatalogued(rule: string | null | undefined): rule is string {
  return typeof rule === 'string' && Object.hasOwn(DENIALS, rule);
}

export function ruleOfRow(r: Pick<WireAuditRow, 'action' | 'outcome'> & { rule?: string | null | undefined }): { rule: string | null; action: string } {
  if (r.outcome !== 'denied') return { rule: null, action: r.action };
  const at = r.action.lastIndexOf('.');
  const tail = at > 0 ? r.action.slice(at + 1) : '';
  const suffixed = isCatalogued(tail);
  if (r.rule) return { rule: r.rule, action: suffixed && tail === r.rule ? r.action.slice(0, at) : r.action };
  return suffixed ? { rule: tail, action: r.action.slice(0, at) } : { rule: null, action: r.action };
}

/** A rule in words, from the gateway's catalogue or core-api's own; null for a code neither knows. */
export function ruleWords(rule: string | null): Denial | null {
  if (!rule) return null;
  if (isCatalogued(rule)) return (DENIALS as Record<string, Denial>)[rule]!;
  return Object.hasOwn(OWN_RULES, rule) ? OWN_RULES[rule]! : null;
}

// What a receipt's target names, by its kind, when the server could not
// resolve it to a name this reader may see.
const TARGET_KINDS: Record<string, string> = {
  agent: 'an agent',
  human: 'a person',
  plan: 'a prospecting plan',
  task: 'a task',
  project: 'a project',
  prospect: 'a place',
  company: 'a company',
  contact: 'a contact',
  thread: 'a conversation',
  message: 'a message',
  draft: 'a draft',
  request: 'a request',
  campaign: 'a campaign',
  mailing: 'a mailing',
  sequence: 'a series of emails',
  change: 'a change',
  build: 'a change',
  promotion: 'a change',
  department: 'a department',
  erasure: 'an erasure request',
  incident: 'an incident',
  assignment: 'an assignment',
  twin_task_plan: 'a task’s plan',
  source: 'a market source',
  market: 'a country',
  index: 'a row of our index',
  signal: 'a signal',
  sample: 'a sample',
  offer: 'an offer',
  supplier: 'a supplier',
  booking: 'a booking',
  mailbox: 'a mailbox',
  box: 'a mailbox',
  connection: 'a service',
  vendor: 'a service',
  setup: 'a setup step',
  step: 'a setup step',
  ledger: 'the ledger',
  killswitch: 'the stop switch',
};

/**
 * What it was done to, as a person reads it. The server resolves what it
 * may (an agent, a person, a department, a plan); anything else is named by
 * its kind, and a tool by what it does. An email address is already words.
 */
export function targetWords(r: Pick<WireAuditRow, 'target'> & { targetLabel?: string | null }): string {
  if (r.targetLabel) return r.targetLabel;
  const t = r.target;
  if (!t) return '—';
  if (/^[^\s:@]+@[^\s:@]+\.[a-z]{2,}$/i.test(t)) return t;
  const [kind = '', rest = ''] = t.split(/:(.*)/s);
  if (kind === 'tool') return toolName(rest.split(':')[0] ?? rest);
  const sub = rest.match(/:tool:([a-z_]+\.[a-z_]+)$/);
  const base = TARGET_KINDS[kind];
  if (base && sub) return `${toolName(sub[1]!)}, for ${base}`;
  if (base) return base;
  if (/^[a-z_]+\.[a-z_]+$/.test(t)) return toolName(t);
  return humanise(kind || t);
}

/** How it ended, in words: done, done under a person's approval code, refused, or failed. */
export function outcomeWords(r: { outcome: string; tier?: string | null }): { label: string; tone: 'accent' | 'danger' | 'warning' | 'neutral' } {
  if (r.outcome === 'ok') return r.tier === 'critical' ? { label: 'DONE · APPROVED BY CODE', tone: 'warning' } : { label: 'DONE', tone: 'accent' };
  return r.outcome === 'denied' ? { label: 'REFUSED', tone: 'danger' } : { label: 'FAILED', tone: 'danger' };
}

// What an approval code lets happen, said before it happens.
const OPERATION_WORDS: Record<string, string> = {
  'agent.restore': 'Restore an agent',
  'agent.revoke': 'Stop an agent',
  'autonomy.change': 'Change how freely a department acts',
  'billing.change': 'Change billing',
  'campaign.approve': 'Approve a campaign',
  'client.admit': 'Connect an app',
  'counterparty.approve': 'Approve a company',
  'data.erase': 'Erase a person’s data',
  'delegation.grant': 'Delegate a mailbox',
  'department.create': 'Create a department',
  'agent.create': 'Add an agent',
  'department.resume': 'Restart a department',
  'department.suspend': 'Stop a department',
  'iam.grant_scope': 'Grant a permission',
  'identity.revoke_human': 'End a person’s access',
  'killswitch.change': 'Change the stop switch',
  'mailing.approve': 'Approve a mailing',
  'project.member': 'Add someone to a project',
  'project.tool_grant': 'Lend a tool to a project',
  'secret.rotate': 'Change a stored key',
  'shortlist.autoconfirm': 'Let shortlists confirm themselves on their record again',
  'operation.run': 'Run a change to the platform',
  'operation.earned_start': 'Let a platform check run on its record again',
  'suppression.lift': 'Write to an address again',
  'task.earned_start': 'Let a routine tool be granted on its record',
  'task.tool_grant': 'Lend a tool to a task',
  'tool.admit': 'Allow a tool',
  'tool.withdraw': 'Stop a tool',
};

/** What an approval code is for, in words: `iam.grant_scope` → "Grant a permission". */
export function operationWords(operation: string): string {
  return OPERATION_WORDS[operation] ?? humanise(operation);
}
